import { type Answer, getAnswer, type Question } from '@strata/react-question-tree';
import { IntakeStatusIndicator } from '@strata/react-status-indicators';

import { Permissions } from 'services/constants';
import { type RawAnswer } from 'store/api/epaApi';
import { FaxType, type IntakeCase } from 'store/api/intakeCaseApi';

/** Check if intake Case has been archived */
export const hasIntakeCaseBeenArchived = (
    intakeStatus?: (typeof IntakeStatusIndicator.Statuses)[keyof typeof IntakeStatusIndicator.Statuses],
) =>
    intakeStatus === IntakeStatusIndicator.Statuses.COMPLETE ||
    intakeStatus === IntakeStatusIndicator.Statuses.CANCELED;

/** Intake Case fields required for prescriber faxes */
export type PrescriberFaxFields = Pick<Partial<IntakeCase>, 'prescriberVerified'>;

/** Does Intake Case have all required fields for sending a prescriber fax? */
export const hasPrescriberFaxFields = (values: PrescriberFaxFields) =>
    [values.prescriberVerified].every((val) => !!val);

/** User can send prescriber fax if they have the permission and if all of required fields are filled out */
export const canSendPrescriberFax = (values: PrescriberFaxFields, permissions: string[]) =>
    (permissions.includes(Permissions.WRITE_INTAKE_CASE_ADMIN) ||
        permissions.includes(Permissions.WRITE_INTAKE_CASE)) &&
    hasPrescriberFaxFields(values);

export const getPrescriberFaxTypeForIntakeCase = (monitoringCase?: boolean) =>
    monitoringCase ? FaxType.PRIOR_AUTH_NOTIFICATION : FaxType.PRESCRIBER_INITIATION;

/** Intake Case fields required for canceling intake cases */
export type CancelIntakeCaseFields = Pick<
    Partial<IntakeCase>,
    'intakeStatus' | 'intakeCaseId' | 'memberFirstName' | 'memberLastName'
>;

/** User can cancel intake case if they have the permission and the intake case has not been archived */
export const canCancelIntakeCase = (values: CancelIntakeCaseFields, permissions: string[]) =>
    !hasIntakeCaseBeenArchived(values.intakeStatus) &&
    permissions.includes(Permissions.WRITE_INTAKE_CASE);

export const mapRawAnswersToFormikValues = (
    rawAnswers: RawAnswer[] | undefined,
    nodes: Question[] | undefined,
): Answer[] => {
    if (!rawAnswers?.length || !nodes?.length) {
        return [];
    }

    const nodeMap: Record<string, Question> = Object.fromEntries(
        nodes.map((node) => [node.question_id.toString(), node]),
    );

    return rawAnswers.map((rawAnswer) => {
        const question = nodeMap[rawAnswer.question_id.toString()];

        if (!question) {
            return {
                question_id: rawAnswer.question_id,
                selectedOptions: rawAnswer.selected_options,
                additionalFreeText: rawAnswer.additional_free_text,
                isInvalid: false,
            };
        }

        let valueInput: string | number | string[] | null | undefined;
        if (rawAnswer.selected_options.length > 1) {
            valueInput = rawAnswer.selected_options.map((o) => o.option_id.toString());
        } else {
            valueInput = rawAnswer.selected_options[0]?.option_id?.toString();
        }
        return getAnswer(question, valueInput, rawAnswer.additional_free_text);
    });
};
